/* ═══════════════════════════════════════════════════════════════
   EVIDENCE — the pure helpers behind EvidenceUpload (design
   2026-09-12 signed-copies-phase4 §4).

   A manager prints a document, signs it with the driver and uploads
   the signed copy; the server keeps it as evidence on the record
   (§2). These helpers only put the words around what the server
   sent and check a file before it goes: the kinds, the type and
   size check in words, the sentence per file ("note-signed.pdf ·
   uploaded 12 Sep by Zed"), the "signed copy on file" line. No
   React, no fetch, no Date.now() unless a `now` is passed. '' / []
   on rubbish, never a throw.
   ═══════════════════════════════════════════════════════════════ */

import { fmtDateWords } from './today.js';
import { personWord } from './ladder.js';

const isObj = (x) => Boolean(x) && typeof x === 'object' && !Array.isArray(x);
const str = (v) => (typeof v === 'string' ? v.trim() : '');

/* ── the kinds ──────────────────────────────────────────────── */

/** The kinds the server accepts (§2; the document family's three added
    by design 2026-09-12 document-family-phase5 §4), each with its word. */
export const KINDS = Object.freeze([
  Object.freeze({ kind: 'signed_note', word: 'signed note' }),
  Object.freeze({ kind: 'signed_warning', word: 'signed warning' }),
  Object.freeze({ kind: 'signed_record', word: 'signed record of discussion' }),
  Object.freeze({ kind: 'signed_invitation', word: 'signed invitation' }),
  Object.freeze({ kind: 'signed_outcome', word: 'signed hearing outcome' }),
  Object.freeze({ kind: 'signed_policy', word: 'signed process document' }),
  Object.freeze({ kind: 'training_certificate', word: 'training certificate' }),
  Object.freeze({ kind: 'other', word: 'file' }),
]);

const BY_KIND = Object.fromEntries(KINDS.map((k) => [k.kind, k]));

/** The word for a kind; '' for anything else. */
export function kindWord(kind) {
  const k = BY_KIND[str(kind).toLowerCase()];
  return k ? k.word : '';
}

/* ── the file check ─────────────────────────────────────────── */

/** What the server accepts (§2) — it sniffs the bytes; this is the
    client's early word so a wrong file never leaves the browser. */
export const ACCEPTED_TYPES = Object.freeze([
  'application/pdf', 'image/jpeg', 'image/png', 'image/webp', 'image/heic',
]);

/** ≤ 10 MB (§2). */
export const MAX_BYTES = 10 * 1024 * 1024;

const EXT_TYPES = Object.freeze({
  pdf: 'application/pdf',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  heic: 'image/heic',
});

const TYPE_WORDS = 'Only PDF, JPEG, PNG, WebP and HEIC files can be uploaded.';

/** The file input's `accept`: the types and their extensions. */
export function acceptAttr() {
  return [...ACCEPTED_TYPES, ...Object.keys(EXT_TYPES).map((e) => `.${e}`)].join(',');
}

/** Bytes as words: 117 KB, 1.4 MB, 10 MB. '' on rubbish. */
export function sizeWords(bytes) {
  if (typeof bytes !== 'number' || !Number.isFinite(bytes) || bytes < 0) return '';
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) {
    const rounded = Math.round(mb * 10) / 10;
    return `${Number.isInteger(rounded) ? rounded : rounded.toFixed(1)} MB`;
  }
  return `${Math.round(bytes / 1024)} KB`;
}

function typeOf(file) {
  const declared = str(file.type).toLowerCase();
  if (declared && declared !== 'application/octet-stream') return declared;
  const name = str(file.name).toLowerCase();
  const ext = name.includes('.') ? name.slice(name.lastIndexOf('.') + 1) : '';
  return EXT_TYPES[ext] || declared;
}

/**
 * '' when the file may go; otherwise the reason in words. Size is
 * checked before type, as the limit is the more useful word when both
 * are wrong. A blank or generic declared type falls back to the
 * extension (a HEIC from a phone often arrives with no type).
 */
export function checkFile(file) {
  if (!isObj(file)) return 'Choose a file first.';
  const size = Number(file.size);
  if (!Number.isFinite(size) || size <= 0) return 'That file is empty.';
  if (size > MAX_BYTES) return `That file is ${sizeWords(size)}; the limit is ${sizeWords(MAX_BYTES)}.`;
  if (!ACCEPTED_TYPES.includes(typeOf(file))) return TYPE_WORDS;
  return '';
}

/* ── the rows ───────────────────────────────────────────────── */

function rowsOf(rows) {
  if (Array.isArray(rows)) return rows;
  if (isObj(rows) && Array.isArray(rows.evidence)) return rows.evidence;
  return [];
}

/** The live rows (status not 'removed'), oldest upload first. [] on rubbish. */
export function liveRows(rows) {
  return rowsOf(rows)
    .filter((r) => isObj(r) && str(r.status).toLowerCase() !== 'removed')
    .map((r, i) => ({ r, i }))
    .sort((a, b) => str(a.r.uploaded_at).localeCompare(str(b.r.uploaded_at)) || a.i - b.i)
    .map(({ r }) => r);
}

/* '12 Sep', with the year when the date is not in `now`'s year (or
   when no clock is given) — the ladder's rule. */
function dateWords(iso, now) {
  const m = str(iso).match(/^\d{4}-\d{2}-\d{2}/);
  const day = m && fmtDateWords(m[0]) ? m[0] : '';
  if (!day) return '';
  const year = Number(day.slice(0, 4));
  const thisYear = now instanceof Date && !isNaN(now.getTime()) && now.getFullYear() === year;
  return `${fmtDateWords(day)}${thisYear ? '' : ` ${year}`}`;
}

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/**
 * One file as a sentence: "note-signed.pdf · uploaded 12 Sep by Zed".
 * Without a filename the kind's word stands in; blank parts are left
 * out; '' on rubbish.
 */
export function evidenceLine(row, now) {
  if (!isObj(row)) return '';
  const name = str(row.filename) || cap(kindWord(row.kind)) || 'File';
  const when = dateWords(row.uploaded_at, now);
  const by = personWord(row.uploaded_by);
  const uploaded = ['uploaded', when, by ? `by ${by}` : ''].filter(Boolean).join(' ');
  return `${name} · ${uploaded}`;
}

/**
 * "signed copy on file" / "2 signed copies on file" / '' from the live
 * rows; `noun` swaps the word ("certificate on file").
 */
export function copyLine(rows, noun = 'signed copy') {
  const n = liveRows(rows).length;
  if (n === 0) return '';
  if (n === 1) return `${noun} on file`;
  const plural = noun.endsWith('y') ? `${noun.slice(0, -1)}ies` : `${noun}s`;
  return `${n} ${plural} on file`;
}

/** The list's empty state for a kind: "No signed copy yet." / "No
    certificate yet." / "No file yet." Only for a list that loaded. */
export function emptyLine(kind) {
  const k = str(kind).toLowerCase();
  if (k.startsWith('signed_')) return 'No signed copy yet.';
  if (k === 'training_certificate') return 'No certificate yet.';
  return 'No file yet.';
}
